import { callOutcomeLabel } from "@/lib/callOutcomeLabel";
import { callActorLabel } from "@/lib/callActorLabel";
import { callNumber } from "@/lib/callNumber";
import { useGetMyLineQuery } from "@/services/softphone";
import CallRecordingPlayer from "@/components/molecules/CallRecordingPlayer";
import { useSoftphone } from "@/contexts/SoftphoneContext";
import CallLog from "@/interfaces/CallLog";
import { useGetCallLogsQuery } from "@/services/callLogs";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  faAddressCard,
  faArrowDown,
  faArrowUp,
  faFilter,
  faPhone,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const correspondentLabel = (row: CallLog): string => {
  if (row.matchedCompany) return row.matchedCompany.name;
  if (row.matchedPerson)
    return `${row.matchedPerson.firstname} ${row.matchedPerson.lastname}`;
  if (row.matchedInstructor)
    return `${row.matchedInstructor.firstname} ${row.matchedInstructor.lastname}`;
  return callNumber(row.remoteNumber) ?? "Numéro indisponible";
};

const correspondentRoute = (row: CallLog): string | null => {
  if (row.matchedCompanyId) return `/companies/${row.matchedCompanyId}`;
  if (row.matchedPersonId) return `/people/${row.matchedPersonId}`;
  if (row.matchedInstructorId) return `/instructors/${row.matchedInstructorId}`;
  return null;
};

const formatDuration = (totalSeconds: number | null): string => {
  if (totalSeconds == null) return "";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const WebphoneRecents: FC = () => {
  const currentUser = useSelector(selectCurrentUser);
  const { data: line } = useGetMyLineQuery();
  const { call, callState } = useSoftphone();
  const [filter, setFilter] = useState<"all" | "out" | "in" | "missed">("all");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<CallLog[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);
  const filterParams = useMemo(
    () => ({
      ...(filter === "out" ? { direction: "out" as const } : {}),
      ...(filter === "in" ? { direction: "in" as const } : {}),
      ...(filter === "missed" ? { missed: true } : {}),
    }),
    [filter],
  );
  const { currentData: data, isFetching } = useGetCallLogsQuery(
    {
      ...(line?.protocolVersion === 2
        ? { softphoneLineId: line.id }
        : { userId: currentUser?.id }),
      pageIndex: page,
      pageSize: 15,
      sortBy: "createdAt",
      sortDirection: "desc",
      ...filterParams,
    },
    {
      pollingInterval: page === 0 ? 5000 : 0,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  useEffect(() => {
    setPage(0);
    setRows([]);
    setHasMore(true);
  }, [filter, currentUser?.id, line?.id, line?.protocolVersion]);

  useEffect(() => {
    if (!data) return;
    setRows((previous) => {
      const updates = new Map(data.data.map((row) => [row.id, row]));
      if (data.meta.currentPage === 1) {
        return [
          ...data.data,
          ...previous.filter((row) => !updates.has(row.id)),
        ];
      }
      const existing = new Set(previous.map((row) => row.id));
      return [
        ...previous.map((row) => updates.get(row.id) ?? row),
        ...data.data.filter((row) => !existing.has(row.id)),
      ];
    });
    setHasMore(data.meta.currentPage < data.meta.lastPage);
  }, [data]);

  const onListScroll = () => {
    const list = listRef.current;
    if (!list || !hasMore || isFetching) return;
    const isAtBottom =
      list.scrollTop + list.clientHeight >= list.scrollHeight - 72;
    if (isAtBottom && list.scrollHeight > list.clientHeight) {
      setPage((current) => current + 1);
    }
  };

  if (isFetching && !rows.length) {
    return (
      <div className="px-3 pb-3 text-center text-body-tertiary">
        <FontAwesomeIcon icon={faSpinner} spin />
      </div>
    );
  }

  const onRecall = (number: string | null) => {
    if (!number) return;
    call(number).catch(() => {
      toast.error("Impossible de passer l'appel");
    });
  };

  return (
    <div
      ref={listRef}
      onScroll={onListScroll}
      className="px-3 pb-3 webphone-recents-list"
    >
      <div
        className="d-flex align-items-center gap-2 mb-2"
        role="group"
        aria-label="Filtrer les appels"
      >
        <FontAwesomeIcon icon={faFilter} className="text-body-tertiary fs--2" />
        <Form.Select
          size="sm"
          value={filter}
          aria-label="Filtrer les appels récents"
          className="webphone-recent-filter-select flex-grow-1"
          onChange={(event) => setFilter(event.target.value as typeof filter)}
        >
          <option value="all">Tous les appels</option>
          <option value="out">Appels sortants</option>
          <option value="in">Appels entrants</option>
          <option value="missed">Appels manqués</option>
        </Form.Select>
      </div>
      {!rows.length && !isFetching && (
        <p className="fs--1 text-body-tertiary mb-2">
          Aucun appel pour ce filtre.
        </p>
      )}
      {rows.map((row) => {
        const route = correspondentRoute(row);
        return (
          <div key={row.id} className="py-2 border-bottom">
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon
                icon={row.direction === "in" ? faArrowDown : faArrowUp}
                className={
                  row.outcome === "failed" || row.outcome === "canceled"
                    ? "text-danger"
                    : "text-body-tertiary"
                }
                fixedWidth
              />
              <div className="flex-grow-1 overflow-hidden">
                <div className="fs--1 fw-semibold text-truncate">
                  {correspondentLabel(row)}
                </div>
                {callNumber(row.remoteNumber) &&
                  correspondentLabel(row) !== callNumber(row.remoteNumber) && (
                    <div className="fs--2 text-body-secondary text-truncate">
                      {callNumber(row.remoteNumber)}
                    </div>
                  )}
                <div className="fs--2 text-body-tertiary">
                  {callActorLabel(row)} ·{" "}
                  {DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy HH:mm")}
                  {row.durationS != null &&
                    ` · ${formatDuration(row.durationS)}`}
                  {row.durationS == null && ` · ${callOutcomeLabel(row)}`}
                </div>
              </div>
              {route && (
                <Link
                  to={route}
                  className="text-body-tertiary p-1"
                  aria-label="Ouvrir la fiche"
                  title="Ouvrir la fiche"
                >
                  <FontAwesomeIcon icon={faAddressCard} />
                </Link>
              )}
              {callNumber(row.remoteNumber) && (
                <Button
                  variant="link"
                  size="sm"
                  className="text-body-tertiary p-1"
                  aria-label="Rappeler"
                  title="Rappeler"
                  disabled={callState !== "idle"}
                  onClick={() => onRecall(callNumber(row.remoteNumber))}
                >
                  <FontAwesomeIcon icon={faPhone} />
                </Button>
              )}
            </div>
            {row.recordingFile?.link && (
              <div className="mt-2">
                <CallRecordingPlayer src={row.recordingFile.link} />
              </div>
            )}
          </div>
        );
      })}
      <div className="text-center py-2" aria-live="polite">
        {isFetching && rows.length > 0 && (
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="text-body-tertiary"
          />
        )}
      </div>
    </div>
  );
};

export default WebphoneRecents;
